import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {HttpResponsesHandlersFactory, IHttpResponseHandlerSettings} from "@bbwt/modules/data-service";
import {PagedCrudService} from "@features/grid";
import {Permissions} from "@main/permissions/permissions";
import {CoreRole, IRole} from "@main/roles";
import {UserHelperService} from "@main/users/user-helper.service";
import {UserTitleMap} from "@main/users/user-title";
import * as moment from "moment";
import {IEpisode} from "@project/models/episodes/episode";
import {IEpisode_Diagnosis} from "../models/episodes/episode_diagnosis";
import {IEpisode_Document} from "@project/models/episodes/episode_document";
import {EpisodeSummary} from "@project/models/episodes/episode-summary";
import {EpisodeStatus} from "@project/models/episodes/episode-status";
import {FileDetails} from "@main/file-storage";
import {IDocument_File} from "@project/models/episodes/document_file_d";
import {EntityId} from "@bbwt/interfaces";
import {NoteService} from "@project/note-shared/classes/note.service";
import {Note} from "@project/models/notes/note";
import {IQueryCommand, QueryCommand, StringFilter} from "@features/filter";
import {SetLocalTimeZone} from "@bbwt/utils";
import {NotificationService} from "@main/notification/notification.service";
import {NotificationType} from "@main/notification/notification-type";
import {UserService} from "@main/users";
import {PatientService} from "@main/app-layout/components/patient-switch/patient.service";
import {PrescriptionLinesService} from "@project/prescription-shared/classes/prescription-lines.service";
import {RavenApiService} from "@main/raven/raven-api.service";
import {IRavenIssue} from "@main/raven/models/raven-issue";
import {RavenNotePlaceholder} from "@main/raven/models/raven-note-placeholder";
import {trimTime} from "@bbwt/utils/date-utils";
import {DocumentState} from "@project/models/episodes/document-state";
import {EpisodeOfflineStorage} from "./episode-offline.storage";
import {WalkInOfflineStorage} from "@project/walk-ins/classes/walk-in-offline.storage";
import {NoteOfflineStorage} from "@project/note-shared/classes/note-offline.storage";
import {AppOnlineStateService} from "@main/pwa/app-online-state.service";
import {GenericDiagnosisCodes} from "@project/diagnosis/generic-codes";
import {HistoricalItem} from "@mipro/classes/historical-item";
import {ConsultationHistoryPageResult} from "@project/models/episodes/consultation-history-page-result";

@Injectable({
    providedIn: "root"
})
export class EpisodeService extends PagedCrudService<IEpisode> {
    public readonly url = "api/episode";
    public readonly entityTitle = "Episode";
    public EpisodeId: number;

    public readonly rolesByPriority = [
        { name: CoreRole.Doctor, shortname: "Doctor" },
        { name: CoreRole.Therapist, shortname: "Therapist" },
        { name: CoreRole.StrengthAndConditioning, shortname: "S&C" },
        { name: CoreRole.Nutritionist, shortname: "Nutritionist" },
        { name: CoreRole.ClinicalAdmin, shortname: "Clinical Admin" },
        { name: CoreRole.NonClinicalAdmin, shortname: "Non Clinical Admin" },
        { name: CoreRole.GovBodyAdmin, shortname: "Governing Body Admin" },
        { name: CoreRole.OrganisationAdmin, shortname: "Org Admin" },
        { name: CoreRole.Player, shortname: "Player" },
        { name: CoreRole.MiProAdmin, shortname: "MiPro Admin" },
        { name: CoreRole.SuperAdmin, shortname: "Super Admin" },
        { name: CoreRole.PPS, shortname: "PPS" },
        { name: CoreRole.GenericPatientLogin, shortname: "Generic Patient Login" }
    ];

    constructor(
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory,
        private userHelperService: UserHelperService,
        private noteService: NoteService,
        private notificationService: NotificationService,
        private userService: UserService,
        private patientService: PatientService,
        private prescriptionLinesService: PrescriptionLinesService,
        private ravenApiService: RavenApiService,
        private appOnlineStateService: AppOnlineStateService
    ) {
        super(http, handlersFactory);
        // We add this instance to NoteService to avoid circular import
        noteService.setEpisodeService(this);
    }

    public async createEpisode(episode: IEpisode, saveAsLocalEpisode = false): Promise<IEpisode> {
        if (this.appOnlineStateService.isAppOnline && !saveAsLocalEpisode) {
            const localEpisode: IEpisode = EpisodeOfflineStorage.remove(episode.id);

            if (localEpisode != null) {
                localEpisode.state = EpisodeStatus.Draft;
                localEpisode.diagnoses = episode.diagnoses;
                localEpisode.id = null;
                localEpisode.documents = null;
                localEpisode.notes = null;
                localEpisode.prescriptions = null;
                localEpisode.userFullName = null;
                localEpisode.userTitle = null;

                await this.notificationService.deleteOfflineNotification(localEpisode.notificationId.toString());
                localEpisode.notificationId = null;

                let newEpisode: IEpisode = await this.handleRequest<any>(
                    this.http.post<any>(`${this.url}/create-episode/${localEpisode.patientUserId}`, localEpisode),
                    this.handlersFactory.getDefault()
                );

                if (this.ravenNoteHandledByServer(localEpisode, newEpisode)) {
                    localEpisode.ravenIssue.ravenNotes = null;
                } else {
                    await this.flushRavenNote(localEpisode.ravenIssue, newEpisode.id, localEpisode.patientUserId);
                }

                this.noteService.updateLocalNotesEpisodeId(episode.id, newEpisode.id);

                const notes: Note[] = NoteOfflineStorage.getEpisodeNotes(newEpisode.id).filter(n => n.prescriptionId == null);
                notes.forEach(n => this.noteService.saveLocalNoteOnline(n.id));

                newEpisode = await this.get(newEpisode.id);

                this.prescriptionLinesService.updateLocalPrescriptionsLinesEpisode(episode.id, newEpisode);
                WalkInOfflineStorage.updateEpisode(episode.id, newEpisode);

                return await this.createRavenNotesWithDate(newEpisode);
            }

            let newEpisode = await this.handleRequest<any>(
                this.http.post<any>(`${this.url}/create-episode/${episode.patientUserId}`, episode),
                this.handlersFactory.getDefault()
            );

            if (this.ravenNoteHandledByServer(episode, newEpisode)) {
                episode.ravenIssue.ravenNotes = null;
            } else {
                const ravenNote = await this.flushRavenNote(episode.ravenIssue, newEpisode.id, episode.patientUserId);
                if (ravenNote) {
                    newEpisode.notes?.push(ravenNote);
                }
            }

            return await this.createRavenNotesWithDate(newEpisode);
        } else {
            episode.state = EpisodeStatus.StoredLocal;
            episode.id = null;
            episode.documents = [];
            episode.notes = [];
            episode.prescriptions = [];
            episode.formInstances = [];

            episode.userId = this.userHelperService.userId;
            episode.userFullName = this.userService.currentUser.fullName;
            episode.userTitle = this.userService.currentUser.title;
            episode.userRole = this.userService.currentUserRoles[0]?.name;

            episode.modifiedUserId = this.userHelperService.userId;
            episode.modifiedUserFullName = this.userService.currentUser.fullName;
            episode.modifiedUserTitle = this.userService.currentUser.title;
            episode.modifiedUserRole = this.userService.currentUserRoles[0]?.name;

            const storedEpisode: IEpisode = EpisodeOfflineStorage.save(episode);
            const notification = await this.notificationService.createOffline({
                id: null,
                title: "Episode - DRAFT OFFLINE for " + this.patientService.currentPatientPlaceHolder.fullName + " pending upload",
                body: null,
                read: false,
                archived: false,
                created: new Date(),
                type: NotificationType.LocalEpisode,
                redirectTo: "/app/consultation-history",
                redirectParams: [
                    {
                        name: "patientId",
                        value: storedEpisode.patientUserId.toString()
                    },
                    {
                        name: "episodeId",
                        value: storedEpisode.id.toString()
                    }
                ],
                savedOffline: true
            });

            storedEpisode.notificationId = notification.id;

            return this.update(storedEpisode.id, storedEpisode);
        }
    }

    public async checkDateUpdateForRaven(oldEpisode: IEpisode, newEpisode: IEpisode): Promise<IEpisode> {
        if (this.ravenApiService.isRavenModuleEnabled() &&
            this.appOnlineStateService.isAppOnline &&
            (
                oldEpisode.createDateTime.getDate() != newEpisode.createDateTime.getDate() ||
                oldEpisode.createDateTime.getMonth() != newEpisode.createDateTime.getMonth() ||
                oldEpisode.createDateTime.getFullYear() != newEpisode.createDateTime.getFullYear())
        ) {
            return await this.createRavenNotesWithDate(newEpisode);
        }
    }

    async createRavenNotesWithDate(episode: IEpisode): Promise<IEpisode> {
        const actualDate = new Date();
        if (
            !this.ravenApiService.isRavenModuleEnabled() ||
            (episode.createDateTime.getDate() == actualDate.getDate() &&
             episode.createDateTime.getMonth() == actualDate.getMonth() &&
             episode.createDateTime.getFullYear() == actualDate.getFullYear())
        ) {
            return episode;
        }

        let dateString = episode.createDateTime.toLocaleDateString();
        let dateStringArray = dateString.split("/");
        dateStringArray = [dateStringArray[1], dateStringArray[0], dateStringArray[2]];

        const newNote = await this.createRavenNote(episode.id, episode.patientUserId, "Episode Date: " + dateStringArray.join("/"));
        episode.notes?.push(newNote);
        return episode;
    }

    ravenNoteHandledByServer(requestEpisode: IEpisode, savedEpisode: IEpisode): boolean {
        const hadIssue = !!requestEpisode?.ravenIssueId && requestEpisode.ravenIssueId !== "0";
        return !!savedEpisode?.ravenIssueId && !hadIssue && !!requestEpisode?.ravenIssue;
    }

    async flushRavenNote(issue: IRavenIssue, episodeId: EntityId, patientId: string): Promise<Note> {
        const text = issue?.ravenNotes;
        if (!text || text.trim() === RavenNotePlaceholder.trim() || !episodeId || episodeId === "0" || !this.appOnlineStateService.isAppOnline) {
            return null;
        }

        const note = await this.createRavenNote(episodeId, patientId, text);
        issue.ravenNotes = null;
        return note;
    }

    async createRavenNote(episodeId: EntityId, patientId: string, text: string, walkInId: EntityId = null): Promise<Note> {
        const note = {
            creationDate: new Date(),
            episodeId: episodeId,
            id: null,
            noteFields: [{ name: "ravenNotes", label: "Raven Notes", value: text, selected: true }],
            patientId: patientId,
            prescriptionId: null,
            selectedTreatments: [],
            state: undefined,
            walkInId: walkInId
        } as Note;

        return await this.noteService.create(note);
    }

    async saveLocalEpisodeOnline(episodeLocalEntityId: string): Promise<IEpisode> {
        if (this.appOnlineStateService.isAppOnline) {
            const localEpisode = await this.getEpisodeData(episodeLocalEntityId, null, null);
            return await this.createEpisode(localEpisode);
        }
    }

    async get(id: EntityId, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IEpisode> {
        if(id == "0" || id == 0){
            return null;
        }

        const episode: IEpisode = EpisodeOfflineStorage.get(id as string);
        return episode ?? super.get(id, responseHandlerSettings);
    }

    async getForDisplay(id: EntityId, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IEpisode> {
        const episode: IEpisode = EpisodeOfflineStorage.get(id as string);
        if (!!episode) return episode;

        return this.httpGet(`${id}/display`, this.handlersFactory.getForReadById(this.entityTitle, responseHandlerSettings));
    }

    async getForPrescription(id: EntityId, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IEpisode> {
        const episode: IEpisode = EpisodeOfflineStorage.get(id as string);
        if (!!episode) return episode;

        return this.httpGet(`${id}/for-prescription`, this.handlersFactory.getForReadById(this.entityTitle, responseHandlerSettings));
    }

    async getForNote(id: EntityId, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IEpisode> {
        const episode: IEpisode = EpisodeOfflineStorage.get(id as string);
        if (!!episode) return episode;

        return this.httpGet(`${id}/for-note`, this.handlersFactory.getForReadById(this.entityTitle, responseHandlerSettings));
    }

    async getEpisodeData(id: EntityId, query: QueryCommand, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IEpisode> {
        const episode: IEpisode = EpisodeOfflineStorage.get(id as string);
        if (episode == null) {
            if (this.extendQueryCommand) {
                this.extendQueryCommand(query);
            }

            return this.handleRequest<IEpisode>(
                this.http.get<IEpisode>(`${this.url}/${id}/get-episode-data`, { params: this.constructHttpParams(query) }),
                this.handlersFactory.getForReadById(this.entityTitle, responseHandlerSettings)
            ).then(result => {
                this.modelUtcDateFields.forEach(utcField => (result[utcField] = SetLocalTimeZone(result[utcField])));
                return result;
            });
        } else {
            return episode;
        }
    }

    async update(id: EntityId, item: IEpisode, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IEpisode> {
        const episode: IEpisode = EpisodeOfflineStorage.get(id as string);

        if (episode == null) {
            const updatedEpisode = await super.update(id, item, responseHandlerSettings);
            this.prescriptionLinesService.updateLocalPrescriptionsLinesEpisode(updatedEpisode.id, updatedEpisode);
            return updatedEpisode;
        } else {
            if (item.state == EpisodeStatus.Archived) {
                return this.deleteLocalEpisode(item.id);
            }

            return EpisodeOfflineStorage.update(item);
        }
    }

    deleteLocalEpisode(id: string): IEpisode {
        const episode: IEpisode = EpisodeOfflineStorage.remove(id);
        if (episode != null) {
            this.notificationService.deleteOfflineNotification(episode.notificationId.toString());
            episode.notificationId = null;
            this.noteService.removeOfflineEpisodeNotes(episode.id);
        }
        return episode;
    }

    public async updateEpisodeDiagnoses(episode: IEpisode): Promise<IEpisode> {
        const localEpisode: IEpisode = EpisodeOfflineStorage.get(episode.id);

        if (localEpisode == null) {
            return await this.handleRequest<any>(
                this.http.post<any>(`${this.url}/update-episode-diagnoses `, episode),
                this.handlersFactory.getForUpdate("entityTitle", { showSuccessMessage: false })
            );
        } else {
            episode.ravenIssueId = episode.ravenIssue?.id;
            localEpisode.ravenIssue = episode.ravenIssue;
            localEpisode.ravenIssueId = episode.ravenIssueId;
            localEpisode.diagnoses = episode.diagnoses;
            localEpisode.createDateTime = episode.createDateTime;
            return EpisodeOfflineStorage.update(localEpisode);
        }
    }

    public updateNoteDraftEpisode(patientId: string): Promise<boolean> {
        return  this.handleRequest<boolean>(
            this.http.post<boolean>(`${this.url}/update-notedraft-episode/${patientId}`,null ),
            this.handlersFactory.getForUpdate("entityTitle", { showSuccessMessage: false })
        );

    }

    public getEpisodes(idUser: string): Promise<IEpisode[]> {
        return this.handleRequest<IEpisode[]>(
            this.http.get<IEpisode[]>(`${this.url}/${idUser}/get-episode`),
            this.handlersFactory.getForReadAll()
        );
    }

    public getEpisodesOnlyDiagnoses(patientId: string): Promise<IEpisode[]> {
        return this.handleRequest<IEpisode[]>(
            this.http.get<IEpisode[]>(`${this.url}/${patientId}/get-episode-onlydiagnoses`),
            this.handlersFactory.getForReadAll()
        );
    }

    public getRecentEpisodes(patientId: string, take: number = 5): Promise<EpisodeSummary[]> {
        return this.handleRequest<EpisodeSummary[]>(
            this.http.get<EpisodeSummary[]>(`${this.url}/${patientId}/get-recent-episodes?take=${take}`),
            this.handlersFactory.getForReadAll()
        ).then(episodes =>
            episodes.map(e => ({
                ...e,
                createDateTime: new Date(e.createDateTime)
            }))
        );
    }

    public createEpisodeDocuments(episodeId: string, documents: IEpisode_Document): Promise<any> {
        return this.handleRequest<any>(
            this.http.post<any>(`${this.url}/${episodeId}/create-episode-documents `, documents),
            this.handlersFactory.getForUpdate("entityTitle", { showSuccessMessage: false })
        );
    }

    public updateDocumentFileTime(documentFileId: string, newDate: Date): Promise<any> {
        return this.handleRequest<any>(
            this.http.post<any>(`${this.url}/update_document_file_creation_date/${documentFileId}`, newDate),
            this.handlersFactory.getForUpdate("File")
        );
    }

    public getDiagnosisEpisode(episodeId: string): Promise<IEpisode_Diagnosis[]> {
        return this.handleRequest<IEpisode_Diagnosis[]>(
            this.http.get<IEpisode_Diagnosis[]>(`${this.url}/${episodeId}/episode-alldiagnos`),
            this.handlersFactory.getForReadAll()
        );
    }

    public getDuplicateEpisodes(episode: IEpisode, patientId: string): Promise<IEpisode[]> {
        return this.handleRequest<IEpisode[]>(
            this.http.post<IEpisode[]>(`${this.url}/${patientId}/check-duplicate-episodes`, episode),
            this.handlersFactory.getForReadAll()
        );
    }

    public getDuplicateBodypartEpisodes(episode: IEpisode, patientId: string): Promise<IEpisode[]> {
        return this.handleRequest<IEpisode[]>(
            this.http.post<IEpisode[]>(`${this.url}/${patientId}/check-duplicate-bodypart-episodes`, episode),
            this.handlersFactory.getForReadAll()
        );
    }

    public findAllDuplicateBodypartEpisodes(episode: IEpisode, patientId: string): Promise<IEpisode[]> {
        return this.handleRequest<IEpisode[]>(
            this.http.post<IEpisode[]>(`${this.url}/${patientId}/find-all-duplicate-bodypart-episodes`, episode),
            this.handlersFactory.getForReadAll()
        );
    }

    public deleteEpisode(episodeId: string, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<void> {
        return this.handleRequest<void>(
            this.http.delete<void>(`${this.url}/${episodeId}/delete-episode`),
            this.handlersFactory.getForDelete("Episode Delete", responseHandlerSettings)
        );
    }

    public mergeEpisodes(sourceEpisodeId: string, targetEpisodeId: string): Promise<void> {
        return this.handleRequest<void>(
            this.http.post<void>(`${this.url}/merge-episodes`, {
                sourceEpisodeId: sourceEpisodeId,
                destinationEpisodeId: targetEpisodeId
            }),
            this.handlersFactory.getForUpdate("Episodes Merged")
        );
    }

    public moveEpisode(
        targetEpisodeId: string,
        movingFromEpisodeId: string,
        documentIds: string[],
        noteIds: string[],
        prescriptionsIds: string[]
    ): Promise<void> {
        const totalSubEntitiesCount = documentIds.length + noteIds.length + prescriptionsIds.length;

        return this.handleRequest<void>(
            this.http.post<void>(`${this.url}/move-sub-entity`, {
                targetEpisodeId: targetEpisodeId,
                movingFromEpisodeId: movingFromEpisodeId,
                documentIds: documentIds,
                noteIds: noteIds,
                prescriptionIds: prescriptionsIds
            }),
            this.handlersFactory.getDefault({ showSuccessMessage: true, successMessage: `${totalSubEntitiesCount} sub-entities moved` })
        );
    }

    public deleteDiagnosisforEpisode(episodeId: string): Promise<void> {
        return this.handleRequest<void>(
            this.http.delete<void>(`${this.url}/${episodeId}/delete-diagnosis`),
            this.handlersFactory.getForDelete("Diagnosis Delete")
        );
    }

    public merge(masterEpisodeId: string, targetEpisodeIds: string[]): Promise<void> {
        return this.handleRequest<void>(
            this.http.post<void>(`${this.url}/merge/${masterEpisodeId}`, targetEpisodeIds),
            this.handlersFactory.getForUpdate("", { successMessage: targetEpisodeIds.length + " episodes have been merged" })
        );
    }

    public unmerge(masterEpisodeId: string, targetEpisodeIds: string[]): Promise<void> {
        return this.handleRequest<void>(
            this.http.post<void>(`${this.url}/unmerge/${masterEpisodeId}`, targetEpisodeIds),
            this.handlersFactory.getForUpdate("Episodes Unmerged")
        );
    }

    public getMergedEpisodes(masterEpisodeId: string) {
        return this.handleRequest<IEpisode[]>(
            this.http.get<IEpisode[]>(`${this.url}/merged/${masterEpisodeId}`),
            this.handlersFactory.getForReadAll()
        );
    }

    public getNoteLastMergedEpisode(noteId: string) {
        return this.handleRequest<IEpisode>(
            this.http.get<IEpisode>(`${this.url}/merged/note/${noteId}`),
            this.handlersFactory.getForReadAll()
        );
    }

    public getPrescriptionLastMergedEpisode(prescriptionId: string) {
        return this.handleRequest<IEpisode>(
            this.http.get<IEpisode>(`${this.url}/merged/prescription/${prescriptionId}`),
            this.handlersFactory.getForReadAll()
        );
    }

    public getDocumentLastMergedEpisode(documentId: string) {
        return this.handleRequest<IEpisode>(
            this.http.get<IEpisode>(`${this.url}/merged/episode-document/${documentId}`),
            this.handlersFactory.getForReadAll()
        );
    }

    public getObservationLastMergedEpisode(observationId: string) {
        return this.handleRequest<IEpisode>(
            this.http.get<IEpisode>(`${this.url}/merged/observation/${observationId}`),
            this.handlersFactory.getForReadAll()
        );
    }

    public updateDocument(document: IEpisode_Document): Promise<void> {
        return this.handleRequest<void>(this.http.post<void>(`${this.url}/update-document`, document));
    }

    public archiveFile(id: string): Promise<void> {
        return this.handleRequest<void>(this.http.post<void>(`${this.url}/archive-file/${id}`, null));
    }

    public deleteFile(id: string): Promise<void> {
        return this.handleRequest<void>(
            this.http.post<void>(`${this.url}/delete-file/${id}`, null),
            this.handlersFactory.getForDelete("Diagnosis Delete")
        );
    }

    public async archive(id: string): Promise<void> {
        const episode: IEpisode = this.deleteLocalEpisode(id);
        if (episode == null) {
            return this.handleRequest<void>(this.http.post<void>(`${this.url}/archive/${id}`, null));
        } else {
            return null;
        }
    }

    public getRole(role: string): string {
        if (!role) {
            return "";
        }
        let roles = role.split("#");

        for (let i = 0; i < this.rolesByPriority.length; i++) {
            if (roles.indexOf(this.rolesByPriority[i].name) != -1) {
                return this.rolesByPriority[i].shortname;
            }
        }
        return role;
    }

    public getPriorityRole(roles: IRole[]): IRole | null {
        for (const roleByPriority of this.rolesByPriority) {
            const matchingRole = roles?.find(role => role.name === roleByPriority.name);
            if (matchingRole) {
                return matchingRole;
            }
        }
        return null;
    }

    getTitle(index: number) {
        let title = UserTitleMap[index];
        if (title == null || title == "Unknown") {
            title = "";
        }
        return title;
    }

    canEditEpisode(episode: IEpisode): boolean {
        if (episode.private) {
            return this.canEditPrivateEpisodes(episode);
        }

        return this.canEditEpisodes();
    }



    canEditPrivateEpisodes(episode: IEpisode): boolean {
        return (this.userHelperService.hasPermission(Permissions.ConsultationHistoryWritePrivateEpisodesUnrestricted) ||
            this.userHelperService.hasPermission(Permissions.ConsultationHistoryPrivateEpisodeWrite) &&
            this.userService.currentUser.id === episode?.assignedClinicianUserId ||
            this.userService.currentUser.id === episode?.userId);
    }

    canEditEpisodes(): boolean {
        return this.userHelperService.hasPermission(Permissions.ConsultationHistoryWrite);
    }

    canMergeEpisode(episode: IEpisode): boolean {
        return (
            episode.state == EpisodeStatus.Active &&
            !episode.private &&
            episode.diagnoses.every(
                d =>
                    d.diagnosisCode !== GenericDiagnosisCodes.GenericPrescriptionDiagnosisCode &&
                    d.diagnosisCode !== GenericDiagnosisCodes.RoutineDiagnosisCode &&
                    d.diagnosisCode !== GenericDiagnosisCodes.GymRecoveryDiagnosisCode
            )
        );
    }

    canCloseIssue(): boolean {
        return this.userHelperService.hasRole(CoreRole.ClinicalAdmin) && this.canEditEpisodes();
    }

    formatDate(date: any): string {
        const parsed = moment(date);
        if (!parsed.isValid() || parsed.year() <= 1) return "Undefined";
        return parsed.format("L LT");
    }

    createEpisodeFiles(episode: IEpisode, documents: FileDetails[], patientId, createDateTime?: Date, isPrivate?: boolean) {
        const files = documents.map(
            file =>
                <IDocument_File>{
                    id: "0",
                    episode_DocumentId: "0",
                    fileDetailsId: file.id,
                    fileCreatedTime: file.uploadTime,
                    fileDetails: null,
                    isArchived: false,
                    createdByFullName: ""
                }
        );

        const documentRibbonId = this.findLastRibbonDocumentId(episode, createDateTime);

        const episodeDocument = <IEpisode_Document>{
            id: documentRibbonId ?? "0",
            episodeId: episode.id,
            patientId: patientId,
            uploadTime: createDateTime,
            state: episode.state == EpisodeStatus.WalkInDraft ? DocumentState.WalkInDraft : DocumentState.Active,
            document_Files: files,
            private: isPrivate
        };

        return this.createEpisodeDocuments(episode.id, episodeDocument);
    }

    searchEpisodesByName(patientId: string, searchTerm: string, movingEpisodeId?: string): Promise<IEpisode[]> {
        const command = <IQueryCommand>{
            filters: [
                new StringFilter("patientId", patientId),
                new StringFilter("movingEpisodeId", movingEpisodeId),
                new StringFilter("searchTerm", searchTerm)
            ]
        };
        const params = this.constructHttpParams(command);
        return this.handleRequest(
            this.http.get<IEpisode[]>(`${this.url}/search-name`, { params }),
            this.handlersFactory.getForReadByFilter({ showErrorMessage: false, showSuccessMessage: false })
        );
    }

    private findLastRibbonDocumentId(episode: IEpisode, date: Date = null) {
        let allSubEntities: { id?: string; time: Date }[] = [
            ...(episode.documents?.map(d => ({ id: d.id, time: new Date(d.uploadTime as any) })) ?? []),
            ...(episode.prescriptions?.map(p => ({ time: p.prescriptionLines[0]?.prescribedDateTime })) ?? []),
            ...(episode.notes?.map(n => ({ time: new Date(n.creationDate) })) ?? [])
        ];

        if (date) {
            allSubEntities = allSubEntities.filter(x => trimTime(x.time).getTime() == trimTime(date).getTime());
        }

        const last = allSubEntities.reduce((p, c) => (p?.time == null || p?.time < c?.time ? c : p), null);

        return last?.id;
    }

    getSelectedEpisodes(newlySelectedEpisodes: any): Promise<IEpisode[]> {
        return this.handleRequest<IEpisode[]>(
            this.http.post<IEpisode[]>(`${this.url}/get-selected-episodes`, newlySelectedEpisodes)
        );
    }

    getAllMedicalRecord(patientId: string, queryCommand: IQueryCommand): Promise<ConsultationHistoryPageResult> {
        return this.handleRequest(
            this.http.get<ConsultationHistoryPageResult>(`${this.url}/${patientId}/medical-record-consultation-history`, {
                params: this.constructHttpParams(queryCommand)
            }),
            this.handlersFactory.getBypass()
        );
    }

    getHistoricalItems(episodeId: string): Promise<HistoricalItem[]> {
        return this.handleRequest<HistoricalItem[]>(
            this.http.get<HistoricalItem[]>(`${this.url}/get-historical-items/${episodeId}`),
            this.handlersFactory.getForReadAll());
    }

    getEpisodeDocumentHistoricalItems(episodeId: string): Promise<HistoricalItem[]> {
        return this.handleRequest<HistoricalItem[]>(
            this.http.get<HistoricalItem[]>(`${this.url}/get-episode-document-historical-items/${episodeId}`),
            this.handlersFactory.getForReadAll());
    }

    public updateEpisodeToActive(episodeId: string, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<void> {
        return this.handleRequest<void>(
            this.http.post<void>(`${this.url}/update-episode-to-active/${episodeId}`, null),
            this.handlersFactory.getForReadAll(responseHandlerSettings)
        );
    }
}
